import React, { useMemo, useRef } from "react";
import ReactHlsPlayer from "react-hls-player";
import type { components } from "../../../apis/gen";
import { BlockButton } from "../../../inJoy-components/Button/BlockButton";
import { Chips } from "../../../inJoy-components/Chips/Chips";
import { PtzCamera } from "../../../inJoy-components/PtzCamera/PtzCamera";
import { Switch } from "../../../inJoy-components/Switch/Switch";
import { isPC } from "../../../utils";
import { EVENT_STATES } from "../useEventState";

type Session = components["schemas"]["Session"];

interface ControlDockTabContentProps {
	eventState: string;
	currentSession: Session | null;
	activeTab: number;
	openFullscreenKiosk: () => Promise<void>;
}

const PLACEHOLDER_IMG = `${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_placeholder_thumbnail.png`;

const ControlDockTabContent = React.memo(
	({
		eventState,
		currentSession,
		activeTab,
		openFullscreenKiosk,
	}: ControlDockTabContentProps) => {
		const playerRef = useRef<HTMLVideoElement>(
			null,
		) as React.RefObject<HTMLVideoElement>;

		// Stabilise the playback URL so the HLS player doesn't remount on every
		// SSE update or currentTime tick – only change when the URL itself changes.
		const playbackUrl = currentSession?.playback_url ?? null;
		const stablePlaybackUrl = useMemo(() => playbackUrl, [playbackUrl]);

		const isSessionActive =
			eventState === EVENT_STATES.PLAYING && !!stablePlaybackUrl;

		// Memoise HLS config so a new object isn't created every render
		const hlsConfig = useMemo(
			() => ({
				maxLoadingDelay: 4,
				minAutoBitrate: 0,
				lowLatencyMode: true,
			}),
			[],
		);

		return (
			<div className="p-5 pb-3 lg:pb-[80px] flex flex-col gap-5">
				<div className="px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
					<p className="text-2xl font-medium text-color-Grey900">
						{isSessionActive ? "Live Stream" : "Camera Control"}
					</p>
					{isSessionActive && stablePlaybackUrl ? (
						<div className="mt-4 aspect-video bg-black rounded-lg overflow-hidden">
							<ReactHlsPlayer
								key={stablePlaybackUrl}
								playerRef={playerRef}
								src={stablePlaybackUrl}
								autoPlay={true}
								controls={true}
								width="100%"
								height="100%"
								poster={PLACEHOLDER_IMG}
								hlsConfig={hlsConfig}
							/>
						</div>
					) : (
						<PtzCamera
							className="lg:mt-9"
							isFocusing={activeTab === 2}
							isPC={isPC()}
						/>
					)}
				</div>
				<div className="flex flex-col lg:flex-row gap-5">
					<div className="flex-[5] px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
						<p className="text-2xl font-medium text-color-Grey900">
							Projection Mode
						</p>
						<div className="mt-5 flex flex-col-reverse lg:flex-row items-stretch lg:items-center gap-x-10 gap-y-5">
							<div className="flex flex-col gap-y-5">
								<div className="flex items-center justify-between">
									<p className="text-base font-medium">
										Enable Projection Mode
									</p>
									<Switch
										checked={false}
										disabled={false}
										onChange={() => undefined}
									/>
								</div>
								<BlockButton
									type="Primary"
									width={`${isPC() ? "400px" : "100%"}`}
									icon="fullscreen"
									size="Large"
									onClick={openFullscreenKiosk}
									disabled={false}
									loading={false}
									loadingIcon=""
								>
									Open Fullscreen Projection
								</BlockButton>
							</div>
							<div className="flex-1">
								<p className="text-base font-medium text-color-Grey700">
									Scene Preview
								</p>
								<div className="relative mt-2.5 aspect-video">
									<div className="w-full h-full flex items-center justify-center bg-[#4D4D4D]">
										<p className="text-base font-medium text-white text-center">
											Turn on Projection Mode to
											<br />
											display the preview
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="flex-[2] px-5 py-6 bg-color-Grey50 rounded-lg border border-color-Grey100">
						<p className="text-2xl font-medium text-color-Grey900">
							System Status
						</p>
						<div className="flex flex-col pt-4">
							<div className="p-3 flex items-center justify-between bg-white border-[#E5E5E5] rounded-[10px]">
								<p className="flex items-center gap-x-2">
									<span className="inline-block w-2 h-2 rounded-md bg-color-Green500"></span>
									<span className="text-sm text-color-Grey900">Server</span>
								</p>
								<Chips
									className="text-color-Green700 border-color-Green100 bg-color-Green50"
									type="Outline"
									label="Online"
									size="Small"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	},
);

export { ControlDockTabContent };
